class PriceRangeSlider extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    
    // Find the price-range element or the section with price inputs
    let priceRangeElement = document.querySelector('price-range');
    if (!priceRangeElement || priceRangeElement.tagName.toLowerCase() !== 'price-range') {
      priceRangeElement = this.parentElement?.querySelector('price-range');
    }
    if (!priceRangeElement) {
      // Look for any price-range element in the same section
      const section = this.closest('.js-filter');
      priceRangeElement = section?.querySelector('price-range');
    }
    
    // If no price-range element, look for inputs directly in the section
    const section = this.closest('.js-filter') || this.closest('form');
    let minInput, maxInput;
    
    if (priceRangeElement) {
      minInput = priceRangeElement.querySelector('input[name*="filter.v.price.gte"]');
      maxInput = priceRangeElement.querySelector('input[name*="filter.v.price.lte"]');
    } else if (section) {
      // Look for price inputs directly in the section
      minInput = section.querySelector('input[data-name*="filter.v.price.gte"]');
      maxInput = section.querySelector('input[data-name*="filter.v.price.lte"]');
    }
    
    if (!minInput || !maxInput) return;
    
    // Get filter parameters
    const minParamName = minInput.name;
    const maxParamName = maxInput.name;
    const rangeMin = 0;
    const rangeMax = parseFloat((maxInput.getAttribute('data-max') || '100000').replace(/,/g, '')) || 100000;
    const currentMin = parseFloat((minInput.value || '0').replace(/,/g, '')) || rangeMin;
    const currentMax = parseFloat((maxInput.value || rangeMax.toString()).replace(/,/g, '')) || rangeMax;
    
    // Get currency symbol - look for it in the price-range element or section
    let currencySymbol = '$';
    let currencyElement = priceRangeElement?.querySelector('.field-currency');
    if (!currencyElement && section) {
      currencyElement = section.querySelector('.field-currency');
    }
    if (currencyElement) {
      currencySymbol = currencyElement.textContent?.trim() || '$';
    }
    
    // Create the slider HTML
    this.innerHTML = `
      <div class="range-container">
        <div class="input-container">
          <div class="low-num-container">
            <input type="number" id="price-low-num-${Date.now()}" class="price-number-input" value="${currentMin}" min="${rangeMin}" max="${rangeMax}" pattern="\d*">
            <span class="currency-symbol">${currencySymbol}</span>
          </div>
          <span class="separator">〜</span>
          <div class="high-num-container">
            <input type="number" id="price-high-num-${Date.now()}" class="price-number-input" value="${currentMax}" min="${rangeMin}" max="${rangeMax}" pattern="\d*">
            <span class="currency-symbol">${currencySymbol}</span>
          </div>
        </div>
        <div class="slider-container">
          <div class="range-bar" id="price-range-bar-${Date.now()}"></div>
          <input type="range" name="${maxParamName}" id="price-high-${Date.now()}" class="high" min="${rangeMin}" max="${rangeMax}" value="${currentMax}">
          <input type="range" name="${minParamName}" id="price-low-${Date.now()}" class="low" min="${rangeMin}" max="${rangeMax}" value="${currentMin}">
        </div>
      </div>
    `;
    
    // Store reference to the price-range element
    this.priceRangeElement = priceRangeElement;
    
    // Store reference to the closest js-filter for proper event handling
    const jsFilter = this.closest('.js-filter');
    this.jsFilterElement = jsFilter;
    
    // Add data attributes to original inputs to identify them as slider-controlled
    const originalMinInput = priceRangeElement.querySelector('input[name*="filter.v.price.gte"]');
    const originalMaxInput = priceRangeElement.querySelector('input[name*="filter.v.price.lte"]');
    if (originalMinInput) {
      originalMinInput.setAttribute('data-price-slider-controlled', 'true');
    }
    if (originalMaxInput) {
      originalMaxInput.setAttribute('data-price-slider-controlled', 'true');
    }
    
    // Initialize the slider
    this.initializeSlider(rangeMin, rangeMax);
  }

  initializeSlider(minPrice, maxPrice) {
    const lowSlider = this.querySelector('input[type="range"].low');
    const highSlider = this.querySelector('input[type="range"].high');
    const lowNum = this.querySelector('.low-num-container input[type="number"]');
    const highNum = this.querySelector('.high-num-container input[type="number"]');
    const rangeBar = this.querySelector('.range-bar');
    
    // Get the form element
    const form = this.closest('form');
    const priceRangeElement = this.priceRangeElement;
    const section = this.closest('.js-filter') || this.closest('form');
    
    // Find original inputs either in price-range element or directly in section
    let originalMinInput, originalMaxInput;
    if (priceRangeElement) {
      originalMinInput = priceRangeElement.querySelector('input[name*="filter.v.price.gte"]');
      originalMaxInput = priceRangeElement.querySelector('input[name*="filter.v.price.lte"]');
    } else if (section) {
      originalMinInput = section.querySelector('input[data-name*="filter.v.price.gte"]');
      originalMaxInput = section.querySelector('input[data-name*="filter.v.price.lte"]');
    }
    
    // Hide original inputs
    if (originalMinInput) originalMinInput.style.display = 'none';
    if (originalMaxInput) originalMaxInput.style.display = 'none';
    
    // Debounced submit function
    let submitTimer;
    const debouncedSubmit = () => {
      clearTimeout(submitTimer);
      submitTimer = setTimeout(() => {
        // Update the original hidden inputs
        if (originalMinInput) originalMinInput.value = lowSlider.value;
        if (originalMaxInput) originalMaxInput.value = highSlider.value;
        
        // Trigger form submission with proper event targeting
        if (form) {
          // Create event with proper target reference for FacetFiltersForm to recognize
          const inputEvent = new Event('input', { 
            bubbles: true, 
            cancelable: true 
          });
          
          // Set the event target to the original price input to help FacetFiltersForm identify it
          const targetInput = originalMinInput || originalMaxInput;
          if (targetInput) {
            Object.defineProperty(inputEvent, 'target', {
              value: targetInput,
              enumerable: true
            });
            
            Object.defineProperty(inputEvent, 'srcElement', {
              value: targetInput,
              enumerable: true
            });
          }
          
          form.dispatchEvent(inputEvent);
        }
      }, 200);
    };
    
    const updateSlider = (event) => {
      let lowVal = parseFloat(lowSlider.value);
      let highVal = parseFloat(highSlider.value);
      
      // Ensure low doesn't exceed high and vice versa
      if (lowVal > highVal) {
        if (event && event.target === lowSlider) {
          lowVal = highVal;
        } else {
          highVal = lowVal;
        }
      }
      
      // Update slider values
      lowSlider.value = lowVal;
      highSlider.value = highVal;
      lowNum.value = lowVal;
      highNum.value = highVal;
      
      // Calculate percentages for the range bar
      const range = maxPrice - minPrice;
      const minPercent = ((lowVal - minPrice) / range) * 100;
      const maxPercent = ((highVal - minPrice) / range) * 100;
      
      // Update range bar position and width
      rangeBar.style.left = minPercent + '%';
      rangeBar.style.width = (maxPercent - minPercent) + '%';
    };
    
    // Update number inputs when sliders change
    const handleSliderInput = (event) => {
      updateSlider(event);
      debouncedSubmit();
    };
    
    // Update sliders when number inputs change
    const handleNumberInput = (event) => {
      const value = parseFloat((event.target.value || '0').replace(/,/g, '')) || 0;
      
      if (event.target === lowNum) {
        lowSlider.value = Math.min(value, parseFloat(highSlider.value));
      } else if (event.target === highNum) {
        highSlider.value = Math.max(value, parseFloat(lowSlider.value));
      }
      
      updateSlider(event);
      debouncedSubmit();
    };
    
    // Add event listeners
    lowSlider.addEventListener('input', handleSliderInput);
    highSlider.addEventListener('input', handleSliderInput);
    lowNum.addEventListener('change', handleNumberInput);
    highNum.addEventListener('change', handleNumberInput);
    
    // Prevent non-numeric input
    [lowNum, highNum].forEach(input => {
      input.addEventListener('keydown', (event) => {
        if (event.metaKey || event.ctrlKey) return;
        const pattern = /[0-9]|\.|Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
        if (!event.key.match(pattern)) event.preventDefault();
      });
    });
    
    // Initialize the slider position
    updateSlider();
  }

  // Method to reinitialize slider after filter updates
  reinitialize() {
    // Get updated values from the form
    const priceRange = this.priceRangeElement;
    const section = this.closest('.js-filter') || this.closest('form');
    
    let minInput, maxInput;
    if (priceRange) {
      minInput = priceRange.querySelector('input[name*="filter.v.price.gte"]');
      maxInput = priceRange.querySelector('input[name*="filter.v.price.lte"]');
    } else if (section) {
      minInput = section.querySelector('input[data-name*="filter.v.price.gte"]');
      maxInput = section.querySelector('input[data-name*="filter.v.price.lte"]');
    }
    
    if (!minInput || !maxInput) return;
    
    const currentMin = parseFloat((minInput.value || '0').replace(/,/g, '')) || parseFloat(minInput.getAttribute('min')) || 0;
    const currentMax = parseFloat((maxInput.value || '0').replace(/,/g, '')) || parseFloat(maxInput.getAttribute('max')) || 100000;
    
    // Update slider values
    const lowSlider = this.querySelector('input[type="range"].low');
    const highSlider = this.querySelector('input[type="range"].high');
    const lowNum = this.querySelector('.low-num-container input[type="number"]');
    const highNum = this.querySelector('.high-num-container input[type="number"]');
    
    if (lowSlider && highSlider) {
      lowSlider.value = currentMin;
      highSlider.value = currentMax;
      if (lowNum) lowNum.value = currentMin;
      if (highNum) highNum.value = currentMax;
      
      // Update visual bar
      const rangeBar = this.querySelector('.range-bar');
      if (rangeBar) {
        const range = parseFloat(lowSlider.max) - parseFloat(lowSlider.min);
        const minPercent = ((currentMin - parseFloat(lowSlider.min)) / range) * 100;
        const maxPercent = ((currentMax - parseFloat(lowSlider.min)) / range) * 100;
        
        rangeBar.style.left = minPercent + '%';
        rangeBar.style.width = (maxPercent - minPercent) + '%';
      }
    }
  }
}

// Define the custom element
customElements.define('price-range-slider', PriceRangeSlider);

// Listen for facet filter resets to update the slider
document.addEventListener('DOMContentLoaded', () => {
  
  // Function to reset slider to default values
  const resetSlider = (slider) => {
    const lowSlider = slider.querySelector('input[type="range"].low');
    const highSlider = slider.querySelector('input[type="range"].high');
    const lowNum = slider.querySelector('.low-num-container input[type="number"]');
    const highNum = slider.querySelector('.high-num-container input[type="number"]');
    const rangeBar = slider.querySelector('.range-bar');
    
    if (lowSlider && highSlider) {
      const minPrice = parseFloat((lowSlider.min || '0').replace(/,/g, '')) || 0;
      const maxPrice = parseFloat((lowSlider.max || '100000').replace(/,/g, '')) || 100000;
      
      lowSlider.value = minPrice;
      highSlider.value = maxPrice;
      if (lowNum) lowNum.value = minPrice;
      if (highNum) highNum.value = maxPrice;
      
      if (rangeBar) {
        rangeBar.style.left = '0%';
        rangeBar.style.width = '100%';
      }
    }
  };
  
  // Function to check if price filter was reset
  const checkPriceReset = (clickedElement) => {
    // Check if this is a price range reset button
    const buttonText = clickedElement.textContent || '';
    const isPriceReset = buttonText.includes('¥') || 
                        buttonText.includes('$') || 
                        buttonText.includes('円') ||
                        buttonText.match(/\d+\s*-\s*\d+/) ||
                        clickedElement.closest('.active-facets__button')?.textContent?.match(/\d+.*-.*\d+/);
    
    return isPriceReset;
  };
  
  // Listen for filter reset events
  document.addEventListener('click', (event) => {
    const clickedElement = event.target;
    
    // Check if this is a facet remove link or clear all button
    const isRemoveButton = clickedElement.closest('facet-remove a') || 
                          clickedElement.closest('.active-facets__button-remove') ||
                          clickedElement.closest('.active-facets__button-inner');
    
    if (isRemoveButton) {
      // Small delay to allow the form to update
      setTimeout(() => {
        const priceRangeSliders = document.querySelectorAll('price-range-slider');
        
        // Check if this is a "clear all" button or price-specific reset
        const isClearAll = clickedElement.closest('.active-facets__button-remove');
        const isPriceReset = checkPriceReset(clickedElement);
        
        if (isClearAll) {
          // Reset all sliders when "clear all" is clicked
          priceRangeSliders.forEach(resetSlider);
        } else if (isPriceReset) {
          // Reset sliders when price-specific reset is clicked
          priceRangeSliders.forEach(resetSlider);
        } else {
          // Check if original inputs were cleared for individual resets
          priceRangeSliders.forEach(slider => {
            // Find inputs either from price-range element or directly from section
            const section = slider.closest('.js-filter') || slider.closest('form');
            let originalMinInput, originalMaxInput;
            
            if (slider.priceRangeElement) {
              originalMinInput = slider.priceRangeElement.querySelector('input[name*="filter.v.price.gte"]');
              originalMaxInput = slider.priceRangeElement.querySelector('input[name*="filter.v.price.lte"]');
            } else if (section) {
              originalMinInput = section.querySelector('input[name*="filter.v.price.gte"]');
              originalMaxInput = section.querySelector('input[name*="filter.v.price.lte"]');
            }
            
            // If inputs are empty, reset the slider
            if ((!originalMinInput?.value || originalMinInput.value === '0') && 
                (!originalMaxInput?.value)) {
              resetSlider(slider);
            }
          });
        }
      }, 100);
    }
  });
});